// WI 34969: a job typeahead can be scoped to the signed-in user's default Viewpoint company
// (options.scopeToUserDefaultCompany), for the NIFTY NCR "job dropdown on the user's default company".

/**
 * A company id no job belongs to. The jobs dropdown treats an absent vpCompanyIDs as "every company
 * the user may see", so a scoped control with no usable default company sends this instead and gets
 * an empty list rather than that superset.
 */
export const DYNAMIC_NO_COMPANY_SCOPE = -1;

/**
 * The extra query params for a job typeahead's company scope, or null when the control is not scoped.
 * `vpCompanyId` is the user's default company (myInfo.Data.VPCompanyID); pass undefined while the
 * profile is still loading, so the control fails closed until it arrives.
 */
export function dynamicJobCompanyScopeParams(
  options: { scopeToUserDefaultCompany?: boolean } | null | undefined,
  vpCompanyId: number | null | undefined
): { vpCompanyIDs: number } | null {
  if (!options?.scopeToUserDefaultCompany) {
    return null;
  }
  const usable =
    typeof vpCompanyId === 'number' &&
    Number.isFinite(vpCompanyId) &&
    vpCompanyId > 0;
  return { vpCompanyIDs: usable ? vpCompanyId : DYNAMIC_NO_COMPANY_SCOPE };
}

/**
 * `params` without the company scope, for re-finding a saved job. The scope is a default for picking,
 * not access control (the server already limits jobs to the user's permitted companies), so a saved
 * job outside the user's default company must still resolve. Returns `params` itself when unscoped,
 * and undefined when nothing else is left.
 */
export function dynamicWithoutCompanyScope<T>(params: T): T | undefined {
  if (!params || typeof params !== 'object' || !('vpCompanyIDs' in params)) {
    return params ?? undefined;
  }
  const rest = { ...(params as Record<string, unknown>) };
  delete rest['vpCompanyIDs'];
  return Object.keys(rest).length ? (rest as T) : undefined;
}
